import { AddCartItem, Cart, CartItem, CartItemLegacy } from '@/types/commerce-types'
import { UseMutateAsyncFunction, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import {
  AddCartItemResponse,
  AddEventItemRequest,
  AddStandardItemRequest,
  AddVariablePricingItemRequest,
  addEventItem,
  addStandardItem,
  addVariablePricingItem,
} from '../api/commerce-api'
import { addCartItemLegacy, removeCartItemLegacy } from '../api/legacy/commerce-legacy-api'
import { logger } from '../lib/logger'
import { useCartHasHydrated, useCartStore, useCartStoreActions } from '../stores/cart-store'
import { useCartQuery } from './use-cart-query'

export const useCart = (): {
  items: CartItem[]
  isLoading: boolean
  error: string | null
  /** @deprecated Use addStandardItem, addEventItem, or addVariablePricingItem instead */
  addItem: UseMutateAsyncFunction<CartItemLegacy[], Error, AddCartItem, unknown>
  /** Add a standard merchandise item to the cart (v2 API) */
  addStandardItem: UseMutateAsyncFunction<AddCartItemResponse, Error, AddStandardItemRequest, unknown>
  /** Add an event ticket to the cart (v2 API) - supports GA, Reserved, Pass */
  addEventItem: UseMutateAsyncFunction<AddCartItemResponse, Error, AddEventItemRequest, unknown>
  /** Add a variable-price item to the cart (v2 API) - e.g., donations */
  addVariablePricingItem: UseMutateAsyncFunction<AddCartItemResponse, Error, AddVariablePricingItemRequest, unknown>
  removeItem: (cartKey: string, itemId: number) => Promise<boolean>
  refetch: () => void
  cart: Cart | undefined
  cartKey: string | undefined
} => {
  const { key, loadingStates, getCartKey } = useCartStore()
  const actions = useCartStoreActions()
  const queryClient = useQueryClient()
  // Wait for store hydration before enabling the query
  // This prevents API calls with stale/undefined cart key during SSR/initial render
  const hasHydrated = useCartHasHydrated()

  // Get cart key once to avoid multiple calls during render
  // NOTE: getCartKey() reads from localStorage["mycart"] FIRST, so it works
  // even before Zustand hydration completes. This is critical for legacy page
  // integration where the cart key is set in localStorage before navigation.
  const currentCartKey = getCartKey()

  const { data, refetch, isLoading, error, failureCount } = useCartQuery({
    cartKey: currentCartKey,
    // Enable query when cart key is available.
    // IMPORTANT: We removed the `hasHydrated` requirement because:
    // 1. getCartKey() reads from localStorage["mycart"] FIRST (before Zustand state)
    // 2. This means even before Zustand hydration, we get a valid key from localStorage
    // 3. Legacy pages (like checkout-cart.html) set localStorage["mycart"] before
    //    navigating to /checkout/payment, so we need to read it immediately
    // 4. The previous `hasHydrated` check caused a race condition where the cart
    //    wouldn't load because hydration happens AFTER first render
    enabled: currentCartKey !== undefined,
    onError: (error) => {
      actions.setError(error.message)
    },
  })

  // Update store when cart data changes
  // Note: actions is intentionally omitted from deps as it's a stable reference from Zustand
  useEffect(() => {
    if (data) {
      actions.setCart(data as unknown as Cart)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  // NOTE: Cart key creation is now handled exclusively by use-cart-query.ts
  // when a cart is not found (404). This prevents race conditions where both
  // use-cart.ts and use-cart-query.ts would create new carts simultaneously.

  // Listen for storage events to sync cart across tabs/legacy pages
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'mycart' && e.newValue !== e.oldValue) {
        logger.info('Cart key changed in storage, refetching cart', {
          oldValue: e.oldValue,
          newValue: e.newValue,
        })
        // Refetch instead of just invalidating to get fresh data
        refetch()
      }
    }

    window.addEventListener('storage', handleStorageChange)

    // Also listen for custom event from legacy pages
    const handleCartUpdate = () => {
      logger.info('Cart update event received, refetching cart')
      // Refetch instead of just invalidating
      refetch()
    }

    window.addEventListener('cartUpdated', handleCartUpdate)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('cartUpdated', handleCartUpdate)
    }
  }, [queryClient, refetch])

  const addMutation = useMutation({
    mutationFn: async (item: AddCartItem) => {
      actions.setLoadingState('add', true)
      try {
        const result = await addCartItemLegacy(item)
        return result
      } finally {
        actions.setLoadingState('add', false)
        queryClient.invalidateQueries({ queryKey: ['cart'] })
      }
    },
  })
  const removeMutation = useMutation({
    mutationFn: async ({ cartKey, itemId }: { cartKey: string; itemId: number }) => {
      actions.setLoadingState('remove', true)
      try {
        logger.info('Removing item from cart', { cartKey, itemId })
        const result = await removeCartItemLegacy(cartKey, itemId)
        return result
      } finally {
        actions.setLoadingState('remove', false)
        queryClient.invalidateQueries({ queryKey: ['cart'] })
      }
    },
  })

  // V2 API Mutations - Modern endpoints for each item type
  // All mutations auto-inject sessionId for cart auto-creation capability
  // Related: PL-500 - Session-based cart persistence
  const addStandardItemMutation = useMutation({
    mutationFn: async (item: AddStandardItemRequest) => {
      actions.setLoadingState('add', true)
      try {
        // Inject sessionId if not provided - enables auto-create cart
        const sessionId = item.sessionId || useCartStore.getState().getSessionId()
        const requestWithSession = { ...item, sessionId }
        logger.info('Adding standard item to cart via v2 API', { itemId: item.itemId, qty: item.qty, sessionId })
        const result = await addStandardItem(requestWithSession)
        return result
      } finally {
        actions.setLoadingState('add', false)
        queryClient.invalidateQueries({ queryKey: ['cart'] })
      }
    },
  })

  const addEventItemMutation = useMutation({
    mutationFn: async (item: AddEventItemRequest) => {
      actions.setLoadingState('add', true)
      try {
        // Inject sessionId if not provided - enables auto-create cart
        const sessionId = item.sessionId || useCartStore.getState().getSessionId()
        const requestWithSession = { ...item, sessionId }
        logger.info('Adding event item to cart via v2 API', { itemId: item.itemId, itemType: item.itemType, sessionId })
        const result = await addEventItem(requestWithSession)
        return result
      } finally {
        actions.setLoadingState('add', false)
        queryClient.invalidateQueries({ queryKey: ['cart'] })
      }
    },
  })

  const addVariablePricingItemMutation = useMutation({
    mutationFn: async (item: AddVariablePricingItemRequest) => {
      actions.setLoadingState('add', true)
      try {
        // Inject sessionId if not provided - enables auto-create cart
        const sessionId = item.sessionId || useCartStore.getState().getSessionId()
        const requestWithSession = { ...item, sessionId }
        logger.info('Adding variable pricing item to cart via v2 API', {
          itemId: item.itemId,
          amount: item.amount,
          sessionId,
        })
        const result = await addVariablePricingItem(requestWithSession)
        return result
      } finally {
        actions.setLoadingState('add', false)
        queryClient.invalidateQueries({ queryKey: ['cart'] })
      }
    },
  })

  // Determine if we're truly loading:
  // 1. Store loadingStates (mutations in progress)
  // 2. React Query isLoading (query in progress)
  // NOTE: We removed the `!hasHydrated` check since getCartKey() reads localStorage
  // first, allowing the query to start immediately when navigating from legacy pages.
  // We also don't include `currentCartKey === undefined` because that's a valid
  // state (user has no cart) and shouldn't show as loading.
  const isTrulyLoading =
    loadingStates.add || loadingStates.remove || loadingStates.fetch || loadingStates.payment || isLoading

  return {
    items: useCartStore((state) => state.items),
    isLoading: isTrulyLoading,
    error: useCartStore((state) => state.error),
    addItem: addMutation.mutateAsync,
    addStandardItem: addStandardItemMutation.mutateAsync,
    addEventItem: addEventItemMutation.mutateAsync,
    addVariablePricingItem: addVariablePricingItemMutation.mutateAsync,
    removeItem: (cartKey: string, itemId: number): Promise<boolean> => removeMutation.mutateAsync({ cartKey, itemId }),
    refetch,
    cart: data as Cart | undefined,
    cartKey: key,
  }
}
